import type { FC } from 'react';
import React, { memo, useCallback } from 'react';
import type { ServiceProviderReceiptBatchRenderRowProps } from './ServiceProviderReceiptBatchReceiptsView';
import { ServiceProviderReceiptBatchRowView } from './ServiceProviderReceiptBatchRowView';
import type { Gender } from '@pflegenavi/shared/api';
import { ImageUploadDropZone } from '@pflegenavi/web-components';
import type { LineItemConfigurationResultDto } from '@pflegenavi/service-provider-shared/api-endpoints';
import { useReceiptBatchStore } from '../ReceiptBatchContext';
import type {
  SetLineItemFieldValue,
  SetReceiptFieldValue,
  TaxItem,
} from './ServiceProviderReceiptBatchForm';
import {
  useAnalyzeReceiptImagePhoenix,
  useGetServiceProviderPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import type { EntryAnalysisValues } from './useEntryReceiptAnalysis';
import { useEntryReceiptAnalysis } from './useEntryReceiptAnalysis';
import { useRowFieldSetter } from './utils/useRowFieldSetter';

interface ReceiptBatchRowContainerProps
  extends ServiceProviderReceiptBatchRenderRowProps {
  upload: (file: File) => Promise<string | undefined>;
  residents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  archivedResidents?: Array<{
    id: string;
    name: string;
    gender: Gender;
  }>;
  lineItemConfigurations: LineItemConfigurationResultDto[] | undefined;
  autoGeneratedReceipts: boolean;
  isLoadingResidents: boolean;
  enqueueSnackbarOnce: (message: string, options?: any) => void;
  showTabTooltip: boolean;
  setShowTabTooltip: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ServiceProviderReceiptBatchRowContainer: FC<ReceiptBatchRowContainerProps> =
  memo(
    ({
      receipt,
      removeRow,
      index,
      setShownImageId,
      setPreviewImageIds,
      residents,
      archivedResidents,
      isLoadingResidents,
      enqueueSnackbarOnce,
      lastInputRef,
      showTabTooltip,
      setShowTabTooltip,
      upload,
      lineItemConfigurations,
      autoGeneratedReceipts,
    }) => {
      // Read from the Formik-driven `receipt` prop, not the store: the store is
      // synced one render late, so after a row above is deleted
      // `receipts[index]` briefly points at a neighbouring row.
      const receiptImageIds = receipt.receiptImageIds;

      const setFieldValue = useReceiptBatchStore(
        (state) => state.formik?.setFieldValue
      )!;
      const { setRowField, getRow } = useRowFieldSetter(
        index,
        receipt,
        setFieldValue
      );

      const setReceiptFieldValue = useCallback<SetReceiptFieldValue>(
        (field, value) => setRowField(field, value),
        [setRowField]
      );

      const setLineItemFieldValue = useCallback<SetLineItemFieldValue>(
        (field, lineItemIndex, value) =>
          setRowField(`lineItems.${lineItemIndex}.${field}`, value),
        [setRowField]
      );

      const setLineItemValues = useCallback(
        (
          lineItemIndex: number,
          name: string,
          amount: number | undefined,
          tax: TaxItem | undefined
        ) => {
          setLineItemFieldValue('name', lineItemIndex, name);
          setLineItemFieldValue('amount', lineItemIndex, amount);
          setLineItemFieldValue(
            'taxes',
            lineItemIndex,
            tax ? [tax] : undefined
          );
        },
        [setLineItemFieldValue]
      );

      const individualDatesMode = useReceiptBatchStore(
        (state) => state.formik?.values.individualDatesMode
      )!;

      const tenant = useReceiptBatchStore(
        (state) =>
          state.nursingHomes.find(
            (n) => n.nursingHomeId === state.formik?.values.nursingHomeId
          )?.tenant
      );
      const analysisEnabled =
        useGetServiceProviderPhoenix().data?.data
          .receipt_image_analysis_enabled ?? false;
      const analyzeReceiptImage = useAnalyzeReceiptImagePhoenix();
      const analyze = useCallback(
        (imageId: string) => analyzeReceiptImage(imageId, tenant ?? ''),
        [analyzeReceiptImage, tenant]
      );
      const setAnalysisValues = useCallback(
        (patch: Partial<EntryAnalysisValues>) => {
          if ('individualDate' in patch) {
            setReceiptFieldValue('individualDate', patch.individualDate);
          }
          if ('receiptNumber' in patch) {
            setReceiptFieldValue('receiptNumber', patch.receiptNumber);
          }
          if ('amount' in patch) {
            setLineItemFieldValue('amount', 0, patch.amount);
          }
        },
        [setReceiptFieldValue, setLineItemFieldValue]
      );
      const analysis = useEntryReceiptAnalysis({
        enabled: analysisEnabled && !!tenant,
        individualDatesMode,
        firstImageId: receiptImageIds?.[0],
        values: {
          individualDate: receipt.individualDate,
          amount: receipt.lineItems[0]?.amount,
          receiptNumber: receipt.receiptNumber,
        },
        analyze,
        setValues: setAnalysisValues,
      });
      const handleSetImages = useCallback(
        (imageIds: string[]) => {
          // We only accept single files at the moment when using drag + drop to be consistent with the file input dialog
          if (imageIds.length !== 1) {
            return;
          }
          // Append to the row's images as of now, not as of the drop: other
          // uploads into this row may have finished in the meantime.
          setReceiptFieldValue('receiptImageIds', [
            ...(getRow()?.receiptImageIds ?? []),
            ...imageIds,
          ]);
        },
        [setReceiptFieldValue, getRow]
      );

      return (
        <ImageUploadDropZone
          upload={upload}
          setImageIds={handleSetImages}
          accept={'image/jpeg,image/png,application/pdf'}
          limit={1}
        >
          <ServiceProviderReceiptBatchRowView
            index={index}
            receipt={receipt}
            individualDatesMode={individualDatesMode}
            showReceiptNumber={analysisEnabled}
            setReceiptFieldValue={setReceiptFieldValue}
            setLineItemFieldValue={setLineItemFieldValue}
            setLineItemValues={setLineItemValues}
            removeRow={removeRow}
            setShownImageId={setShownImageId}
            setPreviewImageIds={setPreviewImageIds}
            lastInputRef={lastInputRef}
            showTabTooltip={showTabTooltip}
            setShowTabTooltip={setShowTabTooltip}
            upload={upload}
            addImages={handleSetImages}
            lineItemConfigurations={lineItemConfigurations}
            autoGeneratedReceipts={autoGeneratedReceipts}
            residents={residents}
            archivedResidents={archivedResidents}
            enqueueSnackbarOnce={enqueueSnackbarOnce}
            isLoadingResidents={isLoadingResidents}
            analysis={analysis}
          />
        </ImageUploadDropZone>
      );
    }
  );
